import cx from 'classnames'
import {
  memo,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo
} from 'react'
import { useOpenFin } from '../../../helpers/hooks/useOpenFin'
import {
  subscribeDepthForSecurity,
  unsubscribeDepthForSecurity
} from '../../../store/depthOfMarket/actions'
import { getDepthForSecurityByType } from '../../../store/depthOfMarket/selectors'
import { cancelOrder } from '../../../store/order/actions'
import { isOrderMine } from '../../../store/order/selectors'
import { Order } from '../../../store/order/types'
import { SecurityStaticData } from '../../../store/securities/reducer'
import { QuoteReliability } from '../../../store/securities/types'
import { useAppDispatch, useAppSelector } from '../../../store/types'
import { openAggressorWindow } from '../../../store/windows/actions'
import { OrderDisplay } from '../helpers'
import BuyOrSellDepth from './BuyOrSellDepth'
import styles from './DepthOfMarket.module.scss'

interface Props {
  callId: string
  security: SecurityStaticData
  footer?: ReactNode
  className?: string
  extraColumns?: OrderDisplay[]
  listId?: number
  hideSpreads?: boolean
  quoteReliability?: QuoteReliability
}

const DepthOfMarket = ({
  security,
  children,
  className,
  callId,
  footer,
  extraColumns,
  listId,
  hideSpreads,
  quoteReliability = QuoteReliability.All
}: PropsWithChildren<Props>) => {
  const { isOpenFin, openPlatformWindow } = useOpenFin()
  const dispatch = useAppDispatch()
  const orderIsMine = useAppSelector(isOrderMine)
  // TODO: custom equality fn for more efficient rendering
  const sellRows = useAppSelector((state) =>
    getDepthForSecurityByType(state)(security.id, 'sell')
  )
  const buyRows = useAppSelector((state) =>
    getDepthForSecurityByType(state)(security.id, 'buy')
  )

  const aggressHandler = useCallback(
    (order: Order) => {
      if (orderIsMine(order.id)) {
        dispatch(cancelOrder(order.id))
      } else {
        if (isOpenFin) {
          void openPlatformWindow(`Aggressor/${order.id}/${security.id}`)
        } else {
          dispatch(openAggressorWindow(order.id, security.id, listId))
        }
      }
    },
    [orderIsMine, dispatch, isOpenFin, openPlatformWindow, security.id, listId]
  )

  const myOrders = useMemo(() => {
    return [...sellRows, ...buyRows]
      .filter((order) => orderIsMine(order.id))
      .map((order) => order.id)
  }, [orderIsMine, sellRows, buyRows])

  useEffect(() => {
    dispatch(
      subscribeDepthForSecurity(
        security.id,
        quoteReliability ?? QuoteReliability.All,
        callId
      )
    )
    return () => {
      dispatch(unsubscribeDepthForSecurity(security.id, callId))
    }
  }, [])

  return (
    <div className={className}>
      {children}
      <div className={styles.depthTable}>
        <BuyOrSellDepth
          type="buy"
          rows={buyRows}
          aggressCallback={aggressHandler}
          securityId={security.id}
          currency={security.currency}
          className={cx(styles.bids, styles.depth)}
          myOrders={myOrders}
          extraColumns={extraColumns}
          hideSpread={hideSpreads}
        />
        <BuyOrSellDepth
          type="sell"
          rows={sellRows}
          aggressCallback={aggressHandler}
          securityId={security.id}
          currency={security.currency}
          className={cx(styles.offers, styles.depth)}
          myOrders={myOrders}
          extraColumns={extraColumns}
          hideSpread={hideSpreads}
        />
      </div>
      {footer}
    </div>
  )
}

export default memo(DepthOfMarket)
